import { currencyCode } from './currencySign';
import { isSandboxEnvironment } from './environment';

function pickBlock(plan) {
    const prices = plan?.prices;
    if (!prices || typeof prices !== 'object') return { block: null, source: 'legacy' };

    const currency = currencyCode().toLowerCase();
    if (prices[currency]) return { block: prices[currency], source: `prices.${currency}` };
    if (prices.usd) return { block: prices.usd, source: 'prices.usd (fallback)' };
    return { block: null, source: 'legacy' };
}

function hasValue(v) {
    return typeof v === 'string' && v.trim() !== '';
}

// In sandbox, prefer block.stripeSandbox IDs; fall back to production
// block.stripe IDs (with a warning) so unconfigured boards keep working.
function pickStripeIds(block, planId) {
    if (!isSandboxEnvironment()) {
        return {
            priceID: block.stripe?.priceID,
            discountID: block.stripe?.discountID,
            trialOfferID: block.stripe?.trialOfferID,
            stripeSource: 'stripe',
        };
    }

    const sandbox = block.stripeSandbox || {};
    if (hasValue(sandbox.priceID)) {
        // Sandbox is configured: use its IDs as-is (an empty discountID means
        // "no coupon" — never mix in a production coupon).
        return {
            priceID: sandbox.priceID,
            discountID: hasValue(sandbox.discountID) ? sandbox.discountID : undefined,
            trialOfferID: hasValue(sandbox.trialOfferID) ? sandbox.trialOfferID : undefined,
            stripeSource: 'stripeSandbox',
        };
    }

    console.warn(
        `[planPrices] plan="${planId ?? '?'}": sandbox environment but no stripeSandbox.priceID configured, falling back to production stripe IDs`
    );
    return {
        priceID: block.stripe?.priceID,
        discountID: block.stripe?.discountID,
        trialOfferID: block.stripe?.trialOfferID,
        stripeSource: 'stripe (fallback)',
    };
}

// In sandbox, prefer block.paddleSandbox IDs; fall back to production
// block.paddle IDs (with a warning) so unconfigured boards keep working.
function pickPaddleIds(block, planId) {
    if (!isSandboxEnvironment()) {
        return { paddle: block.paddle, paddleSource: 'paddle' };
    }

    const sandbox = block.paddleSandbox || {};
    if (hasValue(sandbox.priceId)) {
        // Sandbox is configured: use its IDs as-is (an empty discountId means
        // "no discount" — never mix in a production discount).
        return {
            paddle: {
                priceId: sandbox.priceId,
                discountId: hasValue(sandbox.discountId) ? sandbox.discountId : undefined,
            },
            paddleSource: 'paddleSandbox',
        };
    }

    console.warn(
        `[planPrices] plan="${planId ?? '?'}": sandbox environment but no paddleSandbox.priceId configured, falling back to production paddle IDs`
    );
    return { paddle: block.paddle, paddleSource: 'paddle (fallback)' };
}

function fmt(value) {
    if (value === undefined || value === null || value === '') return '(empty)';
    return value;
}

function logResolution(planId, source, resolved, stripeSource, paddleSource) {
    const stripeLine  = `  stripe[${stripeSource}]: priceID=${fmt(resolved.priceID)}, discountID=${fmt(resolved.discountID)}`;
    const paddleLine  = `  paddle[${paddleSource}]: priceId=${fmt(resolved.paddle?.priceId)}, discountId=${fmt(resolved.paddle?.discountId)}`;
    const solidLine   = `  solidgate: productPriceId=${fmt(resolved.solidgate?.productPriceId)}, couponId=${fmt(resolved.solidgate?.couponId)}`;
    console.log(
        `[planPrices] plan="${planId ?? '?'}" source=${source} env=${isSandboxEnvironment() ? 'sandbox' : 'production'}\n${stripeLine}\n${paddleLine}\n${solidLine}`
    );
}

export function resolvePlanPrices(plan) {
    if (!plan) {
        const empty = { priceID: undefined, discountID: undefined, trialOfferID: undefined, paddle: undefined, solidgate: undefined };
        logResolution(undefined, 'no-plan', empty, 'none', 'none');
        return empty;
    }

    const { block, source } = pickBlock(plan);

    let resolved;
    let stripeSource;
    let paddleSource;
    if (block) {
        const stripeIds = pickStripeIds(block, plan.id);
        stripeSource = stripeIds.stripeSource;
        const paddleIds = pickPaddleIds(block, plan.id);
        paddleSource = paddleIds.paddleSource;
        resolved = {
            priceID: stripeIds.priceID,
            discountID: stripeIds.discountID,
            trialOfferID: stripeIds.trialOfferID,
            paddle: paddleIds.paddle,
            solidgate: block.solidgate,
        };
    } else {
        stripeSource = 'legacy flat';
        paddleSource = 'legacy flat';
        resolved = {
            priceID: plan.priceID,
            discountID: plan.discountID,
            trialOfferID: plan.trialOfferID,
            paddle: plan.paddle,
            solidgate: plan.solidgate,
        };
    }

    logResolution(plan.id, source, resolved, stripeSource, paddleSource);
    return resolved;
}
